import { Button, Separator } from "@elit-hair/elit-ui";
import { isMatch, useMatches } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TbArrowLeft, TbChevronRight } from "react-icons/tb";

import { usePageFilters } from "@/providers/hooks/use-page-filters";
import { router } from "@/router";

import { Link } from "./link";
import { PageFiltersToggle } from "./page-filters-toggle";

export function Breadcrumbs() {
    const matches = useMatches();
    const { t } = useTranslation();
    const { filtersEnabled, filtersOpen, openFilters } = usePageFilters();

    if (matches.some((match) => match.status === "pending")) {
        return null;
    }

    const matchesWithCrumbs = matches.filter((match) => isMatch(match, "staticData.config.breadcrumb"));

    const navigateBack = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        router.history.back();
    };

    if (matchesWithCrumbs.length === 0) {
        return null;
    }

    return (
        <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={navigateBack}
                    aria-label={t("go_back")}
                    disabled={!router.history.canGoBack()}
                >
                    <TbArrowLeft />
                </Button>

                <Separator orientation="vertical" className="h-4 bg-primary/40" />

                {matchesWithCrumbs.map((match, i) => {
                    const isActive = i === matchesWithCrumbs.length - 1;
                    const breadcrumb = match.staticData.config?.breadcrumb;

                    if (!breadcrumb) {
                        return null;
                    }

                    return (
                        <div key={`${match.pathname}-${i}`} className="inline-flex items-center">
                            {!isActive && breadcrumb?.to && (
                                <Link to={breadcrumb.to} asChild>
                                    <Button variant="ghost">{t(breadcrumb.title)}</Button>
                                </Link>
                            )}
                            {isActive && (
                                <Button variant="ghost" disabled>
                                    {t(breadcrumb.title)}
                                </Button>
                            )}

                            {i + 1 < matchesWithCrumbs.length && (
                                <TbChevronRight className="h-4 w-4 transition-transform duration-200" />
                            )}
                        </div>
                    );
                })}
            </div>

            {filtersEnabled && <PageFiltersToggle isOpen={filtersOpen} onOpen={openFilters} />}
        </div>
    );
}
